/**
 * Formata valor como moeda BRL.
 * @param abbreviated  Se true, usa sufixo K/M para valores grandes (ex: R$ 1,2M)
 */
export function formatCurrency(value: number, abbreviated = false): string {
  if (abbreviated) {
    if (Math.abs(value) >= 1_000_000) {
      return `R$ ${formatNumber(value / 1_000_000, 1)}M`;
    }
    if (Math.abs(value) >= 1_000) {
      return `R$ ${formatNumber(value / 1_000, 0)}K`;
    }
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);
}

/**
 * Formata número com separadores de milhar.
 */
export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Formata valor como percentual (ex.: 12,5%). */
export function formatPercent(value: number, decimals = 1): string {
  return `${formatNumber(value, decimals)}%`;
}

/** Data de um instante ISO no calendário de Fortaleza (publicações do Instagram). */
export function formatFortalezaDate(iso: string): string {
  const raw = (iso ?? "").trim();
  if (!raw) return "—";
  if (!raw.includes("T")) return formatDate(raw);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return formatDate(raw);
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Fortaleza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return formatDate(ymd);
}

/**
 * Formata data ISO (yyyy-mm-dd ou com hora) para dd/mm/aaaa.
 */
export function formatDate(isoDate: string): string {
  const raw = (isoDate ?? "").trim().split("T")[0] ?? "";
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d || y.length < 4) return (isoDate ?? "").trim() || "—";
  return `${d}/${m}/${y}`;
}

/** Atalho para datas em cards (ISO ou já dd/mm/yyyy). */
export function formatIsoDateBr(value: string): string {
  const t = (value ?? "").trim();
  if (!t) return "—";
  if (/^\d{2}\/\d{2}\/\d{4}/.test(t)) return t.slice(0, 10);
  return formatDate(t);
}

/** Data/hora local a partir de ISO (ex.: fotos e timeline). */
export function formatDateTime(iso: string): string {
  const d = new Date((iso ?? "").trim());
  if (Number.isNaN(d.getTime())) return (iso ?? "").trim() || "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
